import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { HelpView, HelpWidgetState } from '../types/help.types';

interface HelpContextValue extends HelpWidgetState {
  openHelp: () => void;
  closeHelp: () => void;
  toggleHelp: () => void;
  setView: (view: HelpView) => void;
  setSearchQuery: (query: string) => void;
  selectArticle: (slug: string) => void;
  setChatSession: (sessionId: string | null) => void;
  goBack: () => void;
  currentPage: string;
}

const HelpWidgetContext = createContext<HelpContextValue | undefined>(undefined);

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpWidgetProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const location = useLocation();

  const [state, setState] = useState<HelpWidgetState>({
    isOpen: false,
    view: 'home',
    searchQuery: '',
    selectedArticle: null,
    chatSessionId: null,
  });

  const [_viewHistory, setViewHistory] = useState<HelpView[]>([]);

  const openHelp = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeHelp = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const toggleHelp = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const setView = useCallback((view: HelpView) => {
    setState((prev) => {
      setViewHistory((history) => [...history, prev.view]);
      return { ...prev, view };
    });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setState((prev) => ({
      ...prev,
      searchQuery: query,
      view: query.length > 0 ? 'search' : 'home',
    }));
  }, []);

  const selectArticle = useCallback((slug: string) => {
    setState((prev) => {
      setViewHistory((history) => [...history, prev.view]);
      return { ...prev, selectedArticle: slug, view: 'article' };
    });
  }, []);

  const setChatSession = useCallback((sessionId: string | null) => {
    setState((prev) => ({ ...prev, chatSessionId: sessionId }));
  }, []);

  const goBack = useCallback(() => {
    setViewHistory((history) => {
      const newHistory = [...history];
      const previousView = newHistory.pop() || 'home';
      setState((prev) => ({
        ...prev,
        view: previousView,
        selectedArticle: previousView === 'article' ? prev.selectedArticle : null,
      }));
      return newHistory;
    });
  }, []);

  const value: HelpContextValue = {
    ...state,
    openHelp,
    closeHelp,
    toggleHelp,
    setView,
    setSearchQuery,
    selectArticle,
    setChatSession,
    goBack,
    currentPage: location.pathname,
  };

  return (
    <HelpWidgetContext.Provider value={value}>
      {children}
    </HelpWidgetContext.Provider>
  );
};

export const useHelpContext = (): HelpContextValue => {
  const context = useContext(HelpWidgetContext);
  if (!context) {
    throw new Error('useHelpContext must be used within a HelpWidgetProvider');
  }
  return context;
};

export default HelpWidgetContext;
